// Creature footstep identity and voice: pure data, safe to import without a DOM.
import type { AudioTiers } from './audio-catalog';
import type { FootstepSurfaceClass } from './footstep-surface';

export type FootstepFeet = 'boot' | 'barefoot' | 'hoof' | 'paw' | 'claw' | 'giant' | 'skitter' | 'slither';
export type SizeTier = 'tiny' | 'small' | 'medium' | 'large' | 'huge' | 'gargantuan';
export type FootstepWings = 'leathery' | 'feathered';
const LEATHERY_WINGS: ReadonlySet<string> = new Set(['bat', 'wyvern', 'drake', 'dragon', 'demon', 'devil', 'imp', 'gargoyle',
  'manticore', 'chimera', 'vampire', 'dracolich', 'wyrm']);

export interface FootstepCreature { feet: FootstepFeet; size: SizeTier; wings: FootstepWings }

/** Catalog creature / ancestry names only, as with feet; a character's own name is not anatomy. */
export function footstepWings({ creatureTypeId = '', name = '', ancestryNames = [] }: {
  creatureTypeId?: string; name?: string; ancestryNames?: readonly string[];
}): FootstepWings {
  const type = creatureTypeId.trim().toUpperCase();
  // Whole words, like feet (review 2026-10-14: a substring regex gave "Shrimp", "Mandrake" and "Daredevil" bat wings).
  const leathery = (text: string) => nameWords(text).some(word => wordIn(LEATHERY_WINGS, word));
  return type === '88D96DCB-894B-F4B2-7EEE-4306D63566F2' || type === '208CF3F2-DE5C-77BC-21D5-3654094FE435'
    || leathery(name) || ancestryNames.some(leathery) ? 'leathery' : 'feathered';
}

export const CREATURE_TYPE_FEET: Readonly<Record<string, FootstepFeet>> = Object.freeze({
  '3A28EC64-DA47-09A0-AD1B-31B32627094F': 'boot', // Humanoid
  '3FEEDE53-20B7-DEE8-C64B-8D0738E667A9': 'boot', // Undead
  '5169158E-D7ED-A3E6-7627-A934EBBD9CAD': 'boot', // Celestial
  '492112D6-F574-43B2-9DFE-C37FE82A0763': 'barefoot', // Fey
  '68B30308-92E2-38A9-E73B-5CA95EBFD424': 'paw', // Beast
  '0D7FEDA8-DC2A-6048-5C13-73219F1F0B4F': 'claw', // Monstrosity
  '88D96DCB-894B-F4B2-7EEE-4306D63566F2': 'claw', // Dragon
  '208CF3F2-DE5C-77BC-21D5-3654094FE435': 'claw', // Fiend
  'D1429E13-BDC0-8D8A-011A-522DF622FA2C': 'giant', // Giant
  '01671C87-2DB5-B7C0-6312-FCCD4C20C128': 'slither', // Ooze
  'EDD2EF50-DC2D-75F3-367F-A6A9D63D9316': 'slither', // Aberration
  'FE5CD056-4302-EBB7-BAE4-E8BC0CCC1FCD': 'slither', // Plant
  'BAF03D85-934E-4781-7C5F-26EE18BC8614': 'boot', // Construct
  'D3E7CACD-C46B-1C27-39BC-3E4803D6AED5': 'boot', // Elemental
});

/**
 * Ordered overrides: anatomy wins over the broad creature type. WHOLE WORDS only (review 2026-10-14: substring regexes
 * walked "Bugbear" on paws, "Steel Golem" as an eel, "Scarecrow" on claws). A hyphenated word stays whole, so
 * "Snake-Eye Shaman" is a person, not a snake. Basilisks are legged. A word also matches its plural (-s, -es, -ves).
 */
export const FEET_NAME_WORDS: ReadonlyArray<readonly [FootstepFeet, ReadonlySet<string>]> = Object.freeze([
  ['skitter', new Set(['spider', 'scorpion', 'centipede', 'millipede', 'beetle', 'ant', 'insect', 'crab', 'tick', 'mantis', 'ankheg'])],
  ['slither', new Set(['snake', 'serpent', 'naga', 'worm', 'wurm', 'eel', 'python', 'viper', 'titanoboa', 'constrictor', 'seahorse'])],
  ['hoof', new Set(['horse', 'warhorse', 'pony', 'mule', 'donkey', 'centaur', 'satyr', 'minotaur', 'goat', 'elk', 'deer', 'stag',
    'unicorn', 'ox', 'oxen', 'bull', 'boar', 'camel', 'nightmare', 'pegasus', 'hippogriff', 'ram'])],
  ['claw', new Set(['owlbear', 'bird', 'hawk', 'eagle', 'owl', 'raven', 'crow', 'griffon', 'gryphon', 'raptor', 'lizard', 'drake',
    'wyvern', 'dinosaur', 'aarakocra', 'kenku', 'kobold', 'dragonborn', 'lizardfolk'])],
  ['paw', new Set(['wolf', 'warg', 'worg', 'dog', 'hound', 'cat', 'lion', 'lioness', 'tiger', 'panther', 'leopard', 'jaguar',
    'cougar', 'lynx', 'bear', 'fox', 'jackal', 'hyena', 'rat', 'badger', 'tabaxi', 'weasel', 'wolverine'])],
  ['barefoot', new Set(['monk', 'dryad', 'nymph', 'feral', 'wildling', 'tribesman', 'tribeswoman', 'beggar', 'prisoner', 'ascetic'])],
  ['giant', new Set(['golem', 'colossus', 'titan', 'treant', 'ent', 'behemoth'])],
]);
/** Phrases that override their own words ("Sea Horse" swims; it never clops). */
const FEET_NAME_PHRASES: ReadonlyArray<readonly [FootstepFeet, string]> = Object.freeze([['slither', 'sea horse']]);

function nameWords(name: string): string[] {
  return name.toLowerCase().replace(/'s\b/g, '').split(/[\s,()/.;:]+/).filter(Boolean);
}
function wordIn(set: ReadonlySet<string>, word: string): boolean {
  return set.has(word) || (word.endsWith('ves') && set.has(word.slice(0, -3) + 'f'))
    || (word.endsWith('es') && set.has(word.slice(0, -2))) || (word.endsWith('s') && set.has(word.slice(0, -1)));
}
/** The anatomy a NAME implies, or null. Names are catalog creature / ancestry names — never a character's own name. */
export function feetFromName(name: string): FootstepFeet | null {
  const lower = name.toLowerCase();
  for (const [kind, phrase] of FEET_NAME_PHRASES) if (lower.includes(phrase)) return kind;
  const words = nameWords(name);
  for (const [kind, set] of FEET_NAME_WORDS) if (words.some(word => wordIn(set, word))) return kind;
  return null;
}

export function footstepFeetKind({ creatureTypeId = '', name = '', ancestryNames, size = 'medium' }: {
  creatureTypeId?: string; name?: string; ancestryNames?: readonly string[]; size?: SizeTier;
}): FootstepFeet {
  let feet = CREATURE_TYPE_FEET[creatureTypeId.trim().toUpperCase()] ?? 'boot';
  const named = feetFromName(name) ?? (ancestryNames ?? []).map(feetFromName).find(kind => kind !== null) ?? null;
  if (named) feet = named;
  const big = size === 'large' || size === 'huge' || size === 'gargantuan';
  // A giant stomp needs a giant body: a Medium "Puppet Golem" walks like anyone else (pitched by size).
  if (feet === 'giant' && !big) return 'boot';
  return (feet === 'boot' || feet === 'barefoot') && (size === 'huge' || size === 'gargantuan') ? 'giant' : feet;
}

export const SIZE_VOICES: Readonly<Record<SizeTier, Readonly<{ rate: number; gain: number }>>> = Object.freeze({
  tiny: Object.freeze({ rate: 1.3, gain: .45 }),
  small: Object.freeze({ rate: 1.12, gain: .7 }),
  medium: Object.freeze({ rate: 1, gain: 1 }),
  large: Object.freeze({ rate: .88, gain: 1.15 }),
  huge: Object.freeze({ rate: .78, gain: 1.25 }),
  gargantuan: Object.freeze({ rate: .7, gain: 1.3 }),
});

export function footstepSizeVoice(size: SizeTier = 'medium'): Readonly<{ rate: number; gain: number }> {
  return SIZE_VOICES[size] ?? SIZE_VOICES.medium;
}

export const FEET_POOLS: Readonly<Record<Exclude<FootstepFeet, 'boot'>, string>> = Object.freeze({
  hoof: 'hooves', paw: 'paws', claw: 'claws', giant: 'giant', skitter: 'skitter', slither: 'slither', barefoot: 'barefoot',
});

/** Until owner approval supplies takes, these empty catalog tiers fall through to the surface tiers. */
export function footstepFeetTiers(feet: FootstepFeet, gait: 'walk' | 'run' | 'sprint', surfaceClass: FootstepSurfaceClass | null): AudioTiers {
  if (feet === 'boot' || surfaceClass === 'water' || surfaceClass === 'mud' || surfaceClass === 'snow' || surfaceClass === 'flesh') return [];
  return [[`speed_walking/${gait === 'walk' ? 'walking' : 'running'}_${FEET_POOLS[feet]}`]];
}
